import React, { useCallback, useEffect, useMemo, useState } from "react";
import { plural } from "../plural";
import { useRebaseClient } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";
import { fetchAllTalents } from "./_talents_paging";
import {
    BackToList,
    useAnnouncedSnackbar,
    useMasterDetail,
    workspaceListCls,
    workspaceRootCls,
    WorkspaceTitle,
} from "./_workspace";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    CircularProgressCenter as RebaseCircularProgressCenter,
    FilterChip as RebaseFilterChip,
    TextField as RebaseTextField,
    Typography as RebaseTypography,
    ExternalLinkIcon as RebaseExternalLinkIcon,
    cls,
    cardClickableMixin,
    cardSelectedMixin,
    defaultBorderMixin
} from "@rebasepro/ui";

import { PersonAvatar } from "./_avatar";
import { TalentOverview } from "./TalentOverview";
import { callFunction, type MailboxStatus } from "./_email_shared";
// Two genuinely generic helpers that happen to live in the prospects module
// because that is where they were first needed: "hace 5 días" and "read every
// page of this collection". Imported rather than copied — a second `sinceLabel`
// is one bug waiting for a timezone, which is the reason the first one was
// centralised. Worth lifting into a shared module the day a third screen wants
// them.
import { fetchAllRows, sinceLabel } from "./_prospects_shared";
import {
    TALENT_STAGE_COLOR,
    TALENT_STAGE_LABEL,
    TALENT_TAB_HINT,
    TALENT_TABS,
    buildCandidates,
    closeProcessEmail,
    firstNameOf,
    isChosenSort,
    isTalentTodo,
    orderQueue,
    talentStageOf,
    type ApplicationRow,
    type Candidate,
    type TalentRow,
    type TalentStage,
    type TalentStatus
} from "./_talents_shared";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const CircularProgressCenter = RebaseCircularProgressCenter as React.ComponentType<any>;
const FilterChip = RebaseFilterChip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const ExternalLinkIcon = RebaseExternalLinkIcon as React.ComponentType<any>;

const JOB_LIMIT = 1000;

/**
 * `answer` leads. It used to be second to "Por hacer", which put 38 people
 * who had waited a month for an answer behind 327 "Revisar el perfil" entries
 * that led nowhere. `email` is new: the people whose record Correo created
 * because they wrote to the mailbox — usually to apply — so the team can see
 * that wave as a group.
 */
type Tab = TalentStatus | "todo" | "answer" | "email";

/**
 * The "Cola" view mode of the `talents` collection.
 *
 * Registered in `frontend/src/App.tsx` under the key `"cola"`, which
 * `collections/talents.ts` names in `admin.customViews`. It used to be a
 * top-level `AppView` at `/candidatos` — a second drawer entry for the same
 * rows this collection already had one for.
 *
 * ── Where the rows come from ─────────────────────────────────────────
 *
 * `tableController`, not a fetch of its own: it carries the collection's live
 * query, so the toolbar's search and filters narrow this view the way they
 * narrow the list and the table. A custom view that fetched its own rows would
 * sit under a toolbar whose controls did nothing.
 *
 * The rows themselves are paged in here rather than taken from the
 * controller's single page, because this view's order is derived. A page of 50
 * sorted by `strength_score` is not the first 50 of "whoever has been waiting
 * longest" — it is 50 arbitrary people put in the wrong order, and no amount of
 * local sorting recovers the other 250. So the toolbar owns the *query* and
 * this view owns *how much of it it reads*: `filterValues`, `sortBy` and
 * `searchString` are passed straight through, and `offset` walks until the set
 * is exhausted.
 *
 * Reading the roster whole is affordable at ~318 rows and is the honest thing
 * to do until a query can express `min(applications.created_at)`. What it is
 * not is unbounded-by-accident: a capped single read would show an arbitrary
 * 1000 and undercount every tab, saying nothing.
 *
 * The applications and the vacancy titles are still fetched here, because they
 * are other collections and nothing in the query layer joins them.
 *
 * ── The queue, and why "responder" leads it ──────────────────────────
 *
 * A person who applied to a posting knows exactly how long ago they did it. That
 * is the most perishable thing in this product and it sits at the top of the
 * default tab, ordered by how long they have been waiting. A candidate nobody
 * has triaged is a slower loss and comes second; somebody already contacted with
 * nothing in flight is third.
 *
 * ── What it deliberately does not do ─────────────────────────────────
 *
 * It does not suggest vacancies for a candidate, or candidates for a vacancy.
 * The only link drawn between a person and a job here is one the person made by
 * applying. Naming candidates to an employer is a separate, explicit act that
 * happens in the outreach composer, where a human picks them — see
 * `_shortlist.ts`.
 */
export function TalentsQueue({ collection, tableController, onEntityClick }: any) {
    const client = useRebaseClient<RebaseClient>();
    const { snackbar, liveRegion } = useAnnouncedSnackbar();
    const { detailOpen, openDetail, closeDetail, masterCls, detailCls } = useMasterDetail();

    const [applications, setApplications] = useState<ApplicationRow[]>([]);
    /** Whether `load` has answered once: the default tab must not be chosen before it has. */
    const [applicationsLoaded, setApplicationsLoaded] = useState(false);
    const [jobTitles, setJobTitles] = useState<Map<string, string>>(new Map());
    const [mailbox, setMailbox] = useState<MailboxStatus | null>(null);
    const [error, setError] = useState("");
    const [, setLoading] = useState(false);
    const [tab, setTab] = useState<Tab>("answer");
    /** A tag to narrow the current tab to. One at a time; the chips are the tags in use. */
    const [tagFilter, setTagFilter] = useState<string | null>(null);
    /** Decided once, after the first load — see the effect below. */
    const [tabChosen, setTabChosen] = useState(false);
    /** The "cerrar un proceso" panel, open above the answer queue. */
    const [closing, setClosing] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    /**
     * `lead_status` values this view has just written, laid over the controller's
     * rows until it refetches.
     *
     * The controller owns its data and exposes no way to poke one row, so
     * without this "Marcar como revisado" moved the row in the database and left
     * the chip beside the button saying the old thing.
     */
    const [statusOverlay, setStatusOverlay] = useState<Record<string, TalentStatus>>({});

    const data = (client as any)?.data;

    /**
     * The roster, read whole, in pages.
     *
     * The query still belongs to `tableController` — its `filterValues`,
     * `sortBy` and `searchString` are the toolbar's, and passing them through is
     * what keeps the toolbar's controls meaningful over a view that fetches for
     * itself. What is not borrowed is its single capped page: this view's order
     * is derived over the whole set, so a page of 1000 is not the first 1000 of
     * "whoever has been waiting longest", and no amount of local sorting
     * recovers the rest.
     *
     * Serialised as a string so the effect re-runs when the toolbar changes and
     * not on every render — these are fresh objects each time.
     */
    const [rows, setRows] = useState<TalentRow[] | null>(null);
    const filterValues = tableController?.filterValues;
    const sortBy = tableController?.sortBy;
    const search = tableController?.searchString ?? "";
    // Serialised for the dependency only. The query itself is built from the
    // live objects — see `fetchAllTalents`, where a JSON round-trip of a filter
    // is the bug that made this worth extracting.
    const queryKey = JSON.stringify({ filterValues, sortBy, search });

    /**
     * The toolbar asked for an order, so this view owes it one.
     *
     * The queue's whole reason to exist is a *derived* order — who is waiting,
     * how long — and it used to impose that unconditionally, after passing
     * `sortBy` to the query. So the sort control above the view moved rows on
     * the server and then had every one of them put back: a control that is
     * drawn, is clickable, and does nothing. Worse than not offering it.
     *
     * When somebody sets a sort, the rows arrive in that order and this view
     * keeps them in it — and stops grouping by stage, because a stage heading
     * over rows ordered by name is a heading that means nothing. When nobody
     * has, the derived order stands — and the collection's default sort, which
     * the controller starts from, is nobody having set one (`isChosenSort`).
     */
    const explicitSort = isChosenSort(sortBy, collection?.sort);

    /** The toolbar is narrowing this list — worth saying, since it can look like nothing happened. */
    const filtered = Boolean(
        (filterValues && Object.keys(filterValues).length > 0) || search.trim()
    );

    useEffect(() => {
        if (!data) return;
        let cancelled = false;

        (async () => {
            try {
                const all = await fetchAllTalents(data, { filterValues, sortBy, search });
                if (cancelled) return;
                setRows(all as unknown as TalentRow[]);
                // Cleared on success: a transient failure must not leave an
                // error sitting above rows that loaded perfectly well.
                setError("");
            } catch (err: any) {
                // An empty queue and a failed query must never render as the
                // same zero.
                if (cancelled) return;
                setRows([]);
                setError(err?.message || "No se pudieron cargar los candidatos");
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, queryKey]);

    const talents: TalentRow[] | null = useMemo(() => {
        if (!rows) return null;
        return rows.map(row => ({
            ...row,
            ...(statusOverlay[String(row.id)] ? { lead_status: statusOverlay[String(row.id)] } : {})
        }));
    }, [rows, statusOverlay]);

    const load = useCallback(async () => {
        if (!data) return;
        setLoading(true);
        setError("");
        try {
            const [applicationRows, jobPage] = await Promise.all([
                fetchAllRows<ApplicationRow>(data, "talent-applications"),
                // Titles only, to name the vacancy somebody applied to. The
                // board is ~100 rows; a lookup per application would be 40
                // requests to say four words each.
                data.collection("jobs").find({ limit: JOB_LIMIT })
            ]);
            setApplications(applicationRows);
            setApplicationsLoaded(true);
            const titles = new Map<string, string>();
            for (const job of ((jobPage?.data ?? []) as { id: string; title?: string | null }[])) {
                if (job?.id) titles.set(String(job.id), (job.title ?? "").trim() || "(vacante sin título)");
            }
            setJobTitles(titles);
        } catch (err: any) {
            setError(err?.message || "No se pudieron cargar los candidatos");
        } finally {
            setLoading(false);
        }
    }, [data]);

    useEffect(() => { void load(); }, [load]);

    useEffect(() => {
        callFunction<MailboxStatus>(client, "sync-inbox").then(setMailbox).catch(() => setMailbox(null));
    }, [client]);

    const jobTitle = useCallback(
        (jobId: string | null | undefined) => (jobId ? jobTitles.get(String(jobId)) : undefined) ?? "(vacante desconocida)",
        [jobTitles]
    );

    const candidates = useMemo(
        () => buildCandidates(talents ?? [], applications),
        [talents, applications]
    );

    const stages = useMemo(() => {
        const map = new Map<string, TalentStage>();
        for (const candidate of candidates) map.set(candidate.id, talentStageOf(candidate));
        return map;
    }, [candidates]);

    const stageOfId = useCallback(
        (candidate: Candidate) => stages.get(candidate.id) ?? talentStageOf(candidate),
        [stages]
    );

    const counts = useMemo(() => {
        const c: Record<TalentStatus, number> = { new: 0, contacted: 0, placed: 0 };
        for (const candidate of candidates) c[candidate.status] += 1;
        return c;
    }, [candidates]);

    const todoCount = useMemo(
        () => candidates.filter(c => isTalentTodo(stageOfId(c))).length,
        [candidates, stageOfId]
    );

    const answerCount = useMemo(
        () => candidates.filter(c => stageOfId(c) === "answer").length,
        [candidates, stageOfId]
    );

    const emailCount = useMemo(
        () => candidates.filter(c => c.row.origin === "email").length,
        [candidates]
    );

    /**
     * Open on the people who are waiting, when there are any.
     *
     * The same rule "Empresas que contratan" and Correo follow: a workspace
     * whose default tab is empty reads as an empty product, and one whose
     * default tab is 300 non-tasks reads as a product nobody can finish.
     * Decided once, after the first load, so it never fights the operator's
     * own clicks.
     */
    useEffect(() => {
        if (tabChosen || talents === null || !applicationsLoaded) return;
        setTabChosen(true);
        setTab(answerCount > 0 ? "answer" : "todo");
    }, [tabChosen, talents, applicationsLoaded, answerCount]);

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        const inTab = (c: Candidate) => {
            if (tab === "todo") return isTalentTodo(stageOfId(c));
            if (tab === "answer") return stageOfId(c) === "answer";
            if (tab === "email") return c.row.origin === "email";
            return c.status === tab;
        };

        const shown = candidates
            .filter(inTab)
            .filter(c => !tagFilter || ((c.row.tags ?? []) as string[]).includes(tagFilter))
            .filter(c => !term
                || c.name.toLowerCase().includes(term)
                || (c.email ?? "").toLowerCase().includes(term)
                || ((c.row.tags ?? []) as string[]).some(t => t.includes(term)));

        // Pure, and pinned by `talents-view-contract.test.ts` — including the
        // one case this view got wrong for as long as it existed: with a sort
        // set in the toolbar, the incoming order is the answer.
        return orderQueue(shown, { tab, explicitSort, stageOf: stageOfId });
    }, [candidates, tab, search, stageOfId, explicitSort, tagFilter]);

    /** The tags in use across the roster, most used first. */
    const tagsInUse = useMemo(() => {
        const counts = new Map<string, number>();
        for (const c of candidates) for (const t of ((c.row.tags ?? []) as string[])) counts.set(t, (counts.get(t) ?? 0) + 1);
        return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
    }, [candidates]);

    const visibleByStage = useMemo(() => {
        const map = new Map<TalentStage, number>();
        for (const candidate of visible) {
            const stage = stageOfId(candidate);
            map.set(stage, (map.get(stage) ?? 0) + 1);
        }
        return map;
    }, [visible, stageOfId]);

    const selected = useMemo(
        () => candidates.find(c => c.id === selectedId) ?? null,
        [candidates, selectedId]
    );

    useEffect(() => {
        if (visible.length === 0) {
            setSelectedId(null);
            return;
        }
        if (!selectedId || !visible.some(c => c.id === selectedId)) {
            setSelectedId(visible[0].id);
        }
    }, [visible, selectedId]);

    return (
        <div className={workspaceRootCls}>
            <WorkspaceTitle>Candidatos</WorkspaceTitle>
            {liveRegion}

            {/* ── Candidate list ── */}
            <div className={cls(masterCls, defaultBorderMixin)}>
                {/* No title, no reload, no search box: the collection toolbar
                    above this view draws all three, and drawing them again gave
                    the screen two of each — two search fields that filtered
                    different things being the worst of it. What stays is what
                    the toolbar has no notion of: the count of who is waiting,
                    and the derived tabs. */}
                <div className="p-4 pb-2 flex flex-col gap-3">
                    <Typography variant="body2" color="secondary">
                        {candidates.length} candidatos · {answerCount} esperando respuesta
                        {/* Named, because a filter that matches everybody and a
                            filter that was ignored look identical from here —
                            every candidate in this database is `sin revisar`, so
                            filtering by that status returns all of them and reads
                            like a broken control. */}
                        {/* "del panel" meant the collection toolbar above this
                            view, which is not a thing anybody calls "el panel".
                            Say where it is. */}
                        {filtered && " · hay un filtro puesto en la barra de arriba"}
                        {explicitSort && " · ordenado desde la barra de arriba"}
                    </Typography>

                    <div className="flex flex-wrap gap-1.5">
                        <FilterChip active={tab === "answer"} onClick={() => setTab("answer")}>
                            Esperan respuesta · {answerCount}
                        </FilterChip>
                        <FilterChip active={tab === "todo"} onClick={() => setTab("todo")}>
                            Por hacer · {todoCount}
                        </FilterChip>
                        <FilterChip active={tab === "email"} onClick={() => setTab("email")}>
                            Llegaron por correo · {emailCount}
                        </FilterChip>
                    </div>
                    {/* What the open filter holds, where somebody would ask. */}
                    <Typography variant="body2" color="secondary">
                        {TALENT_TAB_HINT[tab] ?? ""}
                    </Typography>

                    {/*
                        The one thing that empties the answer queue in bulk. A
                        process closes for everyone who applied to it at once,
                        and on 2026-09-06 thirty-eight people had been waiting a
                        month for a sentence the team had already written to
                        the email applicants by hand.
                    */}
                    {tab === "answer" && answerCount > 0 && (
                        <div>
                            <Button size="small" variant={closing ? "filled" : "outlined"} onClick={() => setClosing(v => !v)}>
                                {closing ? "Cerrar el panel" : "Cerrar un proceso…"}
                            </Button>
                        </div>
                    )}

                    {tagsInUse.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <Typography variant="body2" color="secondary" className="w-full">Etiquetas</Typography>
                            {tagsInUse.map(([tag, n]) => (
                                <FilterChip key={tag} active={tagFilter === tag} onClick={() => setTagFilter(tagFilter === tag ? null : tag)}>
                                    {tag} · {n}
                                </FilterChip>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center gap-1.5 flex-wrap">
                        <Typography variant="body2" color="secondary" className="w-full">
                            En qué punto está cada persona
                        </Typography>
                        {TALENT_TABS.map(t => (
                            <FilterChip key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                                {t.label} · {counts[t.id]}
                            </FilterChip>
                        ))}
                    </div>
                </div>

                <div className={workspaceListCls}>
                    {error && <Alert color="error">{error}</Alert>}
                    {closing && tab === "answer" && (
                        <CloseProcessPanel
                            client={client}
                            candidates={candidates.filter(c => stageOfId(c) === "answer")}
                            jobTitle={jobTitle}
                            onDone={async (sent, failed) => {
                                setClosing(false);
                                await load();
                                snackbar.open({
                                    type: failed ? "warning" : "success",
                                    message: failed
                                        ? `${sent} ${plural(sent, "enviado", "enviados")} · ${failed} no se pudieron enviar`
                                        : `Proceso cerrado: ${sent} ${plural(sent, "persona avisada", "personas avisadas")}`
                                });
                            }}
                            onCancel={() => setClosing(false)}
                        />
                    )}
                    {talents === null && !error && <CircularProgressCenter />}
                    {talents !== null && visible.length === 0 && !error && (
                        <div className="flex items-center justify-center py-8 px-4 text-center">
                            <Typography variant="label" color="secondary">
                                {tab === "todo"
                                    ? "No queda nada por hacer aquí"
                                    : tab === "answer" ? "Nadie espera respuesta"
                                    : tab === "email" ? "Nadie ha llegado escribiéndonos todavía" : "Nada aquí"}
                            </Typography>
                        </div>
                    )}
                    {visible.map((candidate, index) => {
                        const stage = stageOfId(candidate);
                        const grouped = tab === "todo" && !explicitSort;
                        const heads = grouped
                            && (index === 0 || stageOfId(visible[index - 1]) !== stage);
                        return (
                            <React.Fragment key={candidate.id}>
                                {heads && (
                                    <div className="flex items-baseline gap-2 px-2 pt-4 pb-1 first:pt-1">
                                        <Typography variant="micro">
                                            {TALENT_STAGE_LABEL[stage]}
                                        </Typography>
                                        <Typography variant="body2" color="secondary">
                                            {visibleByStage.get(stage) ?? 0}
                                        </Typography>
                                    </div>
                                )}
                                <Card
                                    onClick={() => { setSelectedId(candidate.id); openDetail(); }}
                                    className={cls(
                                        "p-3 flex items-start gap-3",
                                        cardClickableMixin,
                                        candidate.id === selectedId && cardSelectedMixin
                                    )}
                                >
                                    {/* Who this is, before what state they are
                                        in. The queue is read by scanning it for
                                        somebody you already spoke to, and a
                                        column of names alone gives that search
                                        nothing to catch on. */}
                                    <PersonAvatar
                                        name={candidate.name}
                                        photoKey={candidate.row.photo_url}
                                        size={36}
                                        className="mt-0.5"
                                    />
                                    <div className="flex flex-col gap-1 min-w-0 grow">
                                        <div className="flex items-center gap-2">
                                            <Typography variant="subtitle2" component="span" className="truncate grow">
                                                {candidate.name}
                                            </Typography>
                                            {candidate.waitingSince && (
                                                <Chip size="small" colorScheme="redLighter">
                                                    espera {sinceLabel(candidate.waitingSince)}
                                                </Chip>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {!grouped && (
                                                <Chip size="small" colorScheme={TALENT_STAGE_COLOR[stage]}>
                                                    {TALENT_STAGE_LABEL[stage]}
                                                </Chip>
                                            )}
                                            {candidate.applications.length > 0 && (
                                                <Chip size="small" colorScheme="grayLighter">
                                                    {candidate.applications.length === 1
                                                        ? "1 candidatura"
                                                        : `${candidate.applications.length} candidaturas`}
                                                </Chip>
                                            )}
                                            {((candidate.row.tags ?? []) as string[]).slice(0, 3).map(t => (
                                                <Chip key={t} size="small" colorScheme="blueLighter">{t}</Chip>
                                            ))}
                                        </div>
                                        <Typography variant="body2" color="secondary" className="truncate">
                                            {[candidate.row.location, candidate.email].filter(Boolean).join(" · ")
                                                || `registrado ${sinceLabel(candidate.row.created_at)}`}
                                        </Typography>
                                    </div>
                                </Card>
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* ── Detail ── */}
            <div className={detailCls}>
                {!selected && (
                    <div className="flex items-center justify-center h-full">
                        <Typography variant="label" color="secondary">Elige un candidato</Typography>
                    </div>
                )}

                {selected && (
                    <>
                    <BackToList label="Candidatos" onClick={closeDetail}/>
                    <TalentOverview
                        row={selected.row}
                        // The queue holds every application and every job title
                        // already, so the cover renders from memory rather than
                        // issuing two queries per candidate the operator clicks.
                        applications={applications}
                        jobTitle={jobTitle}
                        mailbox={mailbox}
                        onStatusChange={(id, status) => setStatusOverlay(prev => ({ ...prev, [id]: status }))}
                        onApplicationsChanged={() => { void load(); }}
                        headerExtra={
                            /* Was an `<a href="/c/talents/{id}">`, which reloaded
                               the app to reach a record this view is already
                               sitting on top of. `onEntityClick` routes through
                               the collection's resolved open mode, so it opens in
                               the side panel over the queue — on the same cover,
                               with the Ficha, Cuestionario and Correo tabs beside
                               it — and closing it leaves the queue where it was. */
                            <button type="button"
                                onClick={() => {
                                    // The controller holds one page and this view
                                    // holds the roster, so the row being opened is
                                    // routinely not in `data`. `onEntityClick`
                                    // wants an Entity — `{ id, path, values }` —
                                    // and the row is the values. `path` is
                                    // borrowed from a real entity when there is
                                    // one so the collection resolves identically.
                                    const known = (tableController?.data ?? [])
                                        .find((e: any) => String(e.id) === selected.id);
                                    const row = (rows ?? []).find(r => String(r.id) === selected.id);
                                    const entity = known ?? (row
                                        ? {
                                            id: selected.id,
                                            path: (tableController?.data ?? [])[0]?.path ?? "talents",
                                            values: row
                                        }
                                        : undefined);
                                    if (entity) onEntityClick?.(entity);
                                }}
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                                ficha completa <ExternalLinkIcon size={12} />
                            </button>
                        }
                    />
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Close one process for everyone still waiting on it.
 *
 * Groups the open applications by vacancy, lets the operator pick one, shows
 * the mail that will go out — the same text the team sent by hand to the
 * email applicants, editable — and then sends it to each person and marks
 * their application `rejected`, one at a time, saying how far it got.
 *
 * One at a time rather than in parallel: each send opens an SMTP session on
 * the company mailbox, and forty at once is how a relay starts refusing. The
 * status is written only after the mail left, so a person is never marked
 * answered without having been.
 */
function CloseProcessPanel({
    client,
    candidates,
    jobTitle,
    onDone,
    onCancel
}: {
    client: any;
    candidates: Candidate[];
    jobTitle: (jobId: string | null | undefined) => string;
    onDone: (sent: number, failed: number) => Promise<void> | void;
    onCancel: () => void;
}) {
    const data = client?.data;

    /** Vacancies with somebody still waiting, largest queue first. */
    const vacancies = useMemo(() => {
        const byJob = new Map<string, { id: string; title: string; people: Candidate[] }>();
        for (const candidate of candidates) {
            for (const application of candidate.openApplications) {
                const id = String(application.job_id ?? "");
                if (!id) continue;
                const entry = byJob.get(id) ?? { id, title: jobTitle(id), people: [] };
                if (!entry.people.some(p => p.id === candidate.id)) entry.people.push(candidate);
                byJob.set(id, entry);
            }
        }
        return [...byJob.values()].sort((a, b) => b.people.length - a.people.length);
    }, [candidates, jobTitle]);

    const [jobId, setJobId] = useState<string>(() => vacancies[0]?.id ?? "");
    const chosen = vacancies.find(v => v.id === jobId) ?? null;
    const template = useMemo(() => closeProcessEmail({ title: chosen?.title ?? "" }), [chosen?.title]);
    const [subject, setSubject] = useState(template.subject);
    const [body, setBody] = useState(template.body);
    useEffect(() => { setSubject(template.subject); setBody(template.body); }, [template]);

    const [progress, setProgress] = useState<{ sent: number; failed: number; total: number } | null>(null);
    const [error, setError] = useState("");

    const send = useCallback(async () => {
        if (!chosen || !data) return;
        const recipients = chosen.people.filter(p => p.email);
        setProgress({ sent: 0, failed: 0, total: recipients.length });
        setError("");
        let sent = 0;
        let failed = 0;
        for (const person of recipients) {
            try {
                // The greeting takes the person's first name; the rest is the
                // text on screen, so what they get is what the operator read.
                const personal = closeProcessEmail({ title: chosen.title, firstName: firstNameOf(person.name) });
                const text = body.replace(/^Hola,?[^\n]*\n/, personal.body.split("\n")[0] + "\n");
                await callFunction(client, "send-email", {
                    to: [person.email],
                    subject,
                    body: text,
                    link: { entity_type: "talent", entity_id: person.id }
                });
                for (const application of person.openApplications.filter(a => String(a.job_id) === chosen.id)) {
                    await data.collection("talent-applications").update(application.id, { status: "rejected" });
                }
                sent += 1;
            } catch (err: any) {
                failed += 1;
                setError(err?.message || "Un envío falló");
            }
            setProgress({ sent, failed, total: recipients.length });
        }
        await onDone(sent, failed);
    }, [chosen, data, client, subject, body, onDone]);

    if (vacancies.length === 0) return null;

    return (
        <Card className={cls("p-3 mb-2 flex flex-col gap-3", defaultBorderMixin)}>
            <Typography variant="subtitle2" component="h3">Cerrar un proceso</Typography>
            <Typography variant="body2" color="secondary">
                Avisa a todas las personas que siguen esperando en una vacante de que el proceso terminó, y cierra sus candidaturas.
            </Typography>

            <div className="flex flex-wrap gap-1.5">
                {vacancies.map(v => (
                    <FilterChip key={v.id} active={v.id === jobId} onClick={() => setJobId(v.id)} disabled={Boolean(progress)}>
                        {v.title} · {v.people.length}
                    </FilterChip>
                ))}
            </div>

            {chosen && (
                <>
                    <TextField size="small" label="Asunto" value={subject} disabled={Boolean(progress)}
                        onChange={(e: any) => setSubject(e.target.value)} />
                    <TextField size="small" label="Mensaje" value={body} multiline minRows={8} disabled={Boolean(progress)}
                        onChange={(e: any) => setBody(e.target.value)} />
                    <Typography variant="body2" color="secondary">
                        «Hola, …» se convierte en el nombre de cada persona. Se enviará a {chosen.people.filter(p => p.email).length} de {chosen.people.length}
                        {chosen.people.some(p => !p.email) && " (el resto no dejó email)"}.
                    </Typography>
                </>
            )}

            {progress && (
                <Typography variant="body2">
                    Enviando… {progress.sent + progress.failed} de {progress.total}
                    {progress.failed > 0 && ` · ${progress.failed} ${plural(progress.failed, "falló", "fallaron")}`}
                </Typography>
            )}
            {error && <Alert color="warning" size="small">{error}</Alert>}

            <div className="flex gap-2 items-center">
                <Button size="small" variant="filled" disabled={!chosen || Boolean(progress)} onClick={() => void send()}>
                    Enviar y cerrar candidaturas
                </Button>
                <Button size="small" variant="text" disabled={Boolean(progress)} onClick={onCancel}>Cancelar</Button>
            </div>
        </Card>
    );
}
